import { builder, Builder } from "@builder.io/react";
import dynamic from "next/dynamic";
import { PublicSettings } from "@/src/config/PublicSettings";

// Replace with your Public API Key
builder.init(PublicSettings.BUILDER_IO_PUBLIC_API_KEY);

export class BuilderIOUtils {
    static builder = builder;
    static async getPage({ slug, locale, preview = false }: { slug: string; locale: string; preview?: boolean }) {
        const content = await BuilderIOUtils.builder
            // Get the page content from Builder with the specified options
            .get(PublicSettings.BUILDER_IO_PAGE_MODEL, {
                userAttributes: {
                    // Use the page path specified in the URL to fetch the content
                    urlPath: slug,
                    locale: locale
                },
                options: {
                    locale: locale
                },
                // NOTE: prerender must stay false. Setting it to true causes empty "no-blocks"
                // output because custom components registered via dynamic() imports are not
                // available in the Node.js getStaticProps environment. BuilderComponent
                // renders the content tree during Next.js SSR/SSG instead, so crawlers
                // still get the full HTML without JS.
                prerender: false,
                staleCacheSeconds: preview ? 1 : undefined,
                preview: preview,
                // includeUnpublished: preview,
                cachebust: preview
            })
            // Convert the result to a promise
            .toPromise();

        return BuilderIOUtils.optimizeContentPayload(content);
    }

    /**
     * Shrinks the Builder content JSON before it becomes a page prop.
     *
     * Next.js Pages Router serialises all getStaticProps props into
     * __NEXT_DATA__, so every byte of this content ships to the client twice:
     * once as SSR-rendered HTML and once as hydration JSON (ENG-17609 — the
     * homepage was ~2.7 MB, ~1 MB of it __NEXT_DATA__).
     */
    static optimizeContentPayload(content: any) {
        if (!content) {
            return content;
        }

        let optimized = BuilderIOUtils.resolveWonABTest(content);
        optimized = BuilderIOUtils.optimizeImagesForSeo(optimized);

        return optimized;
    }

    /**
     * Collapses a finished A/B test (a variation with testRatio 1, i.e. 100%
     * of traffic) into plain content, mirroring how the Builder SDK resolves a
     * chosen variation client-side ({...content, data: variation.data, ...}).
     *
     * Builder serialises every variation into the content JSON, so a finished
     * test doubles the payload: the losing default tree still ships in
     * __NEXT_DATA__ and is SSR-rendered for crawlers, while the winning tree
     * ships a second time inside a <template> tag that a client-side script
     * swaps in. Collapsing the test halves the payload and makes non-JS
     * crawlers see the content users actually get.
     *
     * Active tests (ratios below 1) are left untouched so bucketing and
     * Builder's test stats keep working.
     */
    static resolveWonABTest(content: any) {
        if (!content?.variations || typeof content.variations !== "object") {
            return content;
        }

        const winner: any = Object.values(content.variations).find((variation: any) => variation?.testRatio === 1);

        if (!winner?.data) {
            return content;
        }

        const collapsed = {
            ...content,
            data: winner.data,
            // Keep attributing impressions/conversions to the winning variation
            variationId: winner.id,
            testVariationId: winner.id,
            testVariationName: winner.name || ""
        };
        delete collapsed.variations;

        return collapsed;
    }

    /**
     * Adjusts Image blocks in the content tree for SEO before rendering:
     *
     * - The first content image (the likely LCP element) is set to
     *   highPriority so it renders eager with fetchpriority="high" instead of
     *   Builder's default loading="lazy" (Semrush QW-3).
     * - Images without alt text get an explicit altText of "" so they render
     *   alt="" alongside Builder's role="presentation". Screen readers treat
     *   the two the same, but SEO crawlers flag any <img> with no alt attribute.
     *
     * Mutating the content JSON (rather than post-processing HTML) keeps the
     * server-rendered markup and client hydration identical.
     */
    static optimizeImagesForSeo(content: any) {
        if (!content?.data?.blocks) {
            return content;
        }

        let heroImageFound = false;

        const visit = (node: any): void => {
            if (Array.isArray(node)) {
                node.forEach(visit);
                return;
            }
            if (!node || typeof node !== "object") {
                return;
            }

            if (node.component?.name === "Image") {
                node.component.options = node.component.options ?? {};
                if (!heroImageFound) {
                    heroImageFound = true;
                    node.component.options.highPriority = true;
                    node.component.options.lazy = false;
                }
                if (!node.component.options.altText) {
                    node.component.options.altText = "";
                }
            }

            Object.values(node).forEach(visit);
        };

        visit(content.data.blocks);

        return content;
    }

    static async getAllPageSlugs({ locale, preview = false }: { locale: string; preview?: boolean }) {
        return await BuilderIOUtils.builder.getAll(PublicSettings.BUILDER_IO_PAGE_MODEL, {
            fields: "data.url",
            locale,
            options: { noTargeting: true }
            // includeUnpublished: preview
        });
    }
}

// Register custom components with Builder.io
// This runs at module load time, after builder.init() is called
Builder.registerComponent(
    dynamic(() => import("@/src/components/organisms/SpotQuoteWidgetBuilder")),
    {
        name: "Spot Quote Widget",
        description: "Embeddable quote widget that shows a pet insurance quote form. Includes fallback image support and conditional rendering based on existing quotes.",
        image: "https://tabler-icons.io/static/tabler-icons/icons-png/forms.png",
        inputs: [
            {
                name: "baseUrl",
                type: "string",
                defaultValue: "https://quote.spotpet.com",
                helperText: "Base URL for the quote widget (e.g., https://quote.spotpet.com or https://quote.spotpet.dev)",
                required: false
            },
            {
                name: "redirectUrl",
                type: "string",
                defaultValue: "/quote",
                helperText: "Redirect URL after quote completion. Defaults to /quote which preserves tracking params.",
                required: false
            },
            {
                name: "containerClassName",
                type: "string",
                defaultValue: "bg-background-surface/90 backdrop-blur-[6px] max-w-full rounded-xl md:min-h-[680px] lg:min-h-[660px]",
                helperText: "Tailwind CSS classes for styling the widget container",
                required: false
            },
            {
                name: "fallbackImageUrl",
                type: "file",
                allowedFileTypes: ["jpeg", "jpg", "png", "webp", "svg"],
                helperText: "Image to display when user already has a quote (optional - if not provided, nothing will show)",
                required: false
            },
            {
                name: "fallbackImageAlt",
                type: "string",
                defaultValue: "",
                helperText: 'Alt text for the fallback image. Leave empty for decorative images (renders alt="" with role="presentation" so screen readers skip it)',
                required: false
            },
            {
                name: "fallbackImageClassName",
                type: "string",
                defaultValue: "w-full h-auto",
                helperText: "CSS classes for the fallback image container",
                required: false
            },
            {
                name: "welcomeBackCtaUrl",
                type: "string",
                defaultValue: "https://quote.spotpet.com/forms/quote-ptz-us",
                helperText:
                    "Welcome Back variant: entry URL for the quote app. quoteId is appended automatically; spot-quote-next reads quote.lastStepID and resumes the user at their last step (with a billing-step freshness override after MAX_RETURN_TO_QUOTE_MINUTES).",
                required: false
            }
        ]
    }
);
